import { useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { AlertTriangle } from 'lucide-react';

interface CampaignDeleteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  campaignName?: string;
  isDeleting?: boolean;
}

export const CampaignDeleteDialog = ({
  open,
  onOpenChange,
  onConfirm,
  campaignName,
  isDeleting = false
}: CampaignDeleteDialogProps) => {
  const [confirmChecked, setConfirmChecked] = useState(false);

  const handleConfirm = () => {
    if (confirmChecked) {
      onConfirm();
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setConfirmChecked(false);
    }
    onOpenChange(newOpen);
  };

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-6 w-6 text-destructive" />
            <AlertDialogTitle className="text-destructive">
              Supprimer définitivement la campagne
            </AlertDialogTitle>
          </div>
          <AlertDialogDescription className="space-y-3 text-left">
            <p>
              Vous êtes sur le point de <strong>supprimer définitivement</strong> la campagne
              {campaignName && (
                <span className="font-medium text-foreground"> "{campaignName}"</span>
              )}.
            </p>
            
            <div className="bg-destructive/10 p-3 rounded-md border border-destructive/20">
              <p className="text-sm font-medium text-destructive mb-2">
                ⚠️ Cette action est irréversible et supprimera :
              </p>
              <ul className="text-xs space-y-1 text-muted-foreground">
                <li>• Toutes les réponses des référents</li>
                <li>• Tous les documents et fichiers audio</li>
                <li>• Les rapports générés</li>
                <li>• L'historique des emails</li>
                <li>• Toutes les données liées à cette campagne</li>
              </ul>
            </div>

            <div className="flex items-center space-x-2 mt-4">
              <Checkbox
                id="confirm-delete"
                checked={confirmChecked}
                onCheckedChange={(checked) => setConfirmChecked(checked === true)}
                disabled={isDeleting}
              />
              <label
                htmlFor="confirm-delete"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                Je comprends que cette action est irréversible
              </label>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeleting}>
            Annuler
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={!confirmChecked || isDeleting}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isDeleting ? 'Suppression...' : 'Supprimer définitivement'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};